/**
 * API client for the Daily Puzzle API.
 * Handles fetch calls with proper error handling and typing.
 */

import type {
    DailyResponse,
    AttemptRequest,
    AttemptResponse,
    RevealRequest,
    RevealResponse,
} from '@daily-puzzle/shared';
import { getStoredDeviceId, setStoredDeviceId } from '../lib/deviceId';

// In production, use the Workers API URL; in dev, use the Vite proxy
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Get the browser's IANA timezone string.
 * Falls back to 'UTC' if unavailable.
 */
function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'UTC';
    }
}

/**
 * Build common headers for all API requests.
 * Includes timezone and device ID fallback for identity recovery.
 */
function getCommonHeaders(): HeadersInit {
    const headers: HeadersInit = {
        'X-Timezone': getBrowserTimezone(),
    };

    // Include device ID from localStorage as fallback (in case cookies are missing)
    const storedDeviceId = getStoredDeviceId();
    if (storedDeviceId) {
        headers['X-Device-Id'] = storedDeviceId;
    }

    return headers;
}

/**
 * Cache the device ID from API response to localStorage.
 */
function cacheDeviceId(response: { device_id?: string }): void {
    if (response.device_id) {
        setStoredDeviceId(response.device_id);
    }
}

class ApiError extends Error {
    constructor(
        public status: number,
        public code: string,
        message: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApiError(
            response.status,
            body.error ?? 'unknown_error',
            body.message ?? 'An error occurred'
        );
    }
    return response.json();
}

export async function fetchDaily(date?: string): Promise<DailyResponse> {
    const url = date ? `${API_BASE}/daily?date=${date}` : `${API_BASE}/daily`;
    const response = await fetch(url, {
        headers: getCommonHeaders(),
        credentials: 'include',
    });
    const data = await handleResponse<DailyResponse>(response);
    cacheDeviceId(data);
    return data;
}

export async function submitAttempt(
    date: string,
    moveUci: string,
    idempotencyKey: string
): Promise<AttemptResponse> {
    const body: AttemptRequest = { date, move_uci: moveUci };
    const response = await fetch(`${API_BASE}/attempt`, {
        method: 'POST',
        headers: {
            ...getCommonHeaders(),
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
        },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    const data = await handleResponse<AttemptResponse>(response);
    cacheDeviceId(data);
    return data;
}

export async function revealPuzzle(
    date: string,
    idempotencyKey: string
): Promise<RevealResponse> {
    const body: RevealRequest = { date };
    const response = await fetch(`${API_BASE}/reveal`, {
        method: 'POST',
        headers: {
            ...getCommonHeaders(),
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
        },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    return handleResponse<RevealResponse>(response);
}

export { ApiError };
